import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Refresh, Schedule, Search as SearchIcon } from '@mui/icons-material'
import type { SchedulerOverview } from '@numeo/types'
import { Spinner } from '../_ds/Primitives'
import { schedulersApi } from './api'
import { formatRelative, formatTime, isFailing, JobFilter, matchesFilter } from './format'
import { JobRow } from './JobRow'
import { RunsTimeline } from './RunsTimeline'

type View = 'jobs' | 'timeline'

const VIEWS: { value: View; label: string }[] = [
    { value: 'jobs', label: 'Jobs' },
    { value: 'timeline', label: 'Timeline' },
]

/** The page re-reads the overview this often while it is open. */
const REFRESH_EVERY_MS = 30_000

const FILTERS: { value: JobFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Running' },
    { value: 'failing', label: 'Failing' },
    { value: 'not_started', label: 'Not started' },
]

/**
 * Every job search-scheduler can run: whether this deployment started it, its schedule, what it did last, when it
 * runs next, and its outcomes over the run history window. Rows expand into the job's recorded runs. The Timeline
 * tab draws every run of every job on one time axis instead.
 */
export default function SchedulersPage() {
    const [view, setView] = useState<View>('jobs')
    return (
        <div className="w-full p-6">
            <div className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
                <span>Home</span>
                <span>/</span>
                <span>Logs</span>
                <span>/</span>
                <span className="font-medium text-gray-600">Schedulers</span>
            </div>

            <div className="mb-4 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
                    <Schedule sx={{ fontSize: 18 }} className="text-indigo-600" />
                </div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Schedulers</h1>
            </div>

            <div className="mb-4 flex gap-1 border-b border-gray-200 dark:border-slate-700">
                {VIEWS.map(({ value, label }) => (
                    <button
                        key={value}
                        onClick={() => setView(value)}
                        className={[
                            '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                            view === value ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700',
                        ].join(' ')}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {view === 'jobs' ? <JobsView /> : <RunsTimeline />}
        </div>
    )
}

/** The job list: each job's live state and outcome counts, expanding into its runs. */
function JobsView() {
    const [overview, setOverview] = useState<SchedulerOverview | null>(null)
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState<JobFilter>('all')
    const [search, setSearch] = useState('')
    const [expanded, setExpanded] = useState<string | null>(null)
    const [now, setNow] = useState(Date.now())

    // One request at a time: a slow search-scheduler must not stack polls on top of each other.
    const inFlight = useRef(false)

    const load = useCallback(async () => {
        if (inFlight.current) return
        inFlight.current = true
        setLoading(true)
        try {
            setOverview(await schedulersApi.overview())
            setError('')
        } catch {
            setError('Could not reach search-scheduler')
        } finally {
            inFlight.current = false
            setNow(Date.now())
            setLoading(false)
        }
    }, [])

    // Polls only while the tab is visible, and catches up the moment it is shown again.
    useEffect(() => {
        load()
        const id = setInterval(() => {
            if (!document.hidden) load()
        }, REFRESH_EVERY_MS)
        const onVisible = () => {
            if (!document.hidden) load()
        }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            clearInterval(id)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [load])

    const jobs = useMemo(() => overview?.jobs ?? [], [overview])
    const counts = useMemo(
        () => ({
            all: jobs.length,
            active: jobs.filter((job) => job.status === 'waiting' || job.status === 'running').length,
            failing: jobs.filter(isFailing).length,
            not_started: jobs.filter((job) => job.status === 'not_started').length,
        }),
        [jobs]
    )
    const visible = useMemo(() => jobs.filter((job) => matchesFilter(job, filter, search)), [jobs, filter, search])

    return (
        <div>
            <div className="mb-4 flex items-center gap-3">
                <div className="flex-1">
                    <p className="text-sm text-gray-500">
                        Every job search-scheduler runs, and what each did in the last {overview?.historyHours ?? 6} hours.
                        {overview && ` Process started ${formatTime(overview.processStartedAt)}${overview.revision ? ` · ${overview.revision}` : ''}.`}
                    </p>
                </div>
                <button
                    onClick={load}
                    className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                    {loading ? <Spinner size={14} /> : <Refresh sx={{ fontSize: 16 }} />}
                    Refresh
                </button>
            </div>

            {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}
            {overview && !overview.historyAvailable && (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    The run history could not be read. Live state is shown; the outcome counts are empty.
                </div>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-2">
                {FILTERS.map(({ value, label }) => (
                    <button
                        key={value}
                        onClick={() => setFilter(value)}
                        className={[
                            'rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors',
                            filter === value ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50',
                        ].join(' ')}
                    >
                        {label} <span className="opacity-70">{counts[value]}</span>
                    </button>
                ))}
                <div className="relative ml-auto">
                    <SearchIcon sx={{ fontSize: 16 }} className="pointer-events-none absolute left-2.5 top-2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search jobs or endpoints"
                        className="h-8 w-64 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm outline-none focus:border-indigo-300"
                    />
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <table className="w-full text-sm">
                    <thead className="border-b border-gray-100 text-left text-[11px] uppercase tracking-wider text-gray-400 dark:border-slate-700">
                        <tr>
                            <th className="px-3 py-2 font-semibold">Job</th>
                            <th className="px-3 py-2 font-semibold">Schedule</th>
                            <th className="px-3 py-2 font-semibold">Status</th>
                            <th className="px-3 py-2 font-semibold">Last run</th>
                            <th className="px-3 py-2 font-semibold">Next run</th>
                            <th className="px-3 py-2 font-semibold" title="Succeeded / failed / timed out / skipped">
                                Last {overview?.historyHours ?? 6} h
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                        {visible.map((job) => (
                            <JobRow
                                key={job.name}
                                job={job}
                                now={now}
                                historyHours={overview?.historyHours ?? 6}
                                expanded={expanded === job.name}
                                onToggle={() => setExpanded(expanded === job.name ? null : job.name)}
                            />
                        ))}
                        {overview && visible.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-sm text-gray-400">
                                    No jobs match.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {!overview && loading && (
                    <div className="flex justify-center py-10">
                        <Spinner size={20} />
                    </div>
                )}
            </div>
            {overview && <p className="mt-2 text-xs text-gray-400">Updated {formatRelative(overview.generatedAt, now)}. Refreshes every 30 s.</p>}
        </div>
    )
}
